import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Clock, Users, Calendar, TrendingUp, AlertCircle, X } from 'lucide-react';
import { toast } from 'sonner';
import { authFetch, API } from '../utils/api';

function Dashboard({ user }) {
  const navigate = useNavigate();
  const [currentEntry, setCurrentEntry] = useState(null);
  const [summary, setSummary] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [pendingLeaves, setPendingLeaves] = useState([]);
  const [showLeaveBanner, setShowLeaveBanner] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const requests = [
        authFetch(`${API}/time/current`),
        authFetch(`${API}/reports/summary`),
        authFetch(`${API}/time/history?limit=5`)
      ];

      if (user.role === 'admin') {
        requests.push(authFetch(`${API}/leave/all`));
      }

      const responses = await Promise.all(requests);
      const [currentRes, summaryRes, historyRes, leavesRes] = responses;

      const current = await currentRes.json();
      const summaryData = await summaryRes.json();
      const history = await historyRes.json();

      setCurrentEntry(current);
      setSummary(summaryData);
      setRecentActivity(history);

      if (leavesRes) {
        const leaves = await leavesRes.json();
        const pending = leaves.filter(l => l.status === 'pending');
        setPendingLeaves(pending);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClockIn = async () => {
    try {
      const response = await authFetch(`${API}/time/clock-in`, {
        method: 'POST'
      });

      if (response.ok) {
        const entry = await response.json();
        setCurrentEntry(entry);
        toast.success('Clocked in successfully!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to clock in');
      }
    } catch (error) {
      toast.error('Failed to clock in');
    }
  };

  const handleClockOut = async () => {
    try {
      const response = await authFetch(`${API}/time/clock-out`, {
        method: 'POST'
      });

      if (response.ok) {
        const entry = await response.json();
        toast.success(`Clocked out! Duration: ${Math.floor(entry.duration / 60)}h ${entry.duration % 60}m`);
        setCurrentEntry(null);
        fetchDashboardData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to clock out');
      }
    } catch (error) {
      toast.error('Failed to clock out');
    }
  };

  const handleApprove = async (leaveId) => {
    try {
      const response = await authFetch(`${API}/leave/${leaveId}/approve`, {
        method: 'POST'
      });

      if (response.ok) {
        toast.success('Leave approved');
        fetchDashboardData();
      } else {
        toast.error('Failed to approve leave');
      }
    } catch (error) {
      toast.error('Failed to approve leave');
    }
  };

  const handleReject = async (leaveId) => {
    try {
      const response = await authFetch(`${API}/leave/${leaveId}/reject`, {
        method: 'POST'
      });

      if (response.ok) {
        toast.success('Leave rejected');
        fetchDashboardData();
      } else {
        toast.error('Failed to reject leave');
      }
    } catch (error) {
      toast.error('Failed to reject leave');
    }
  };

  const formatDuration = (clockIn) => {
    const start = new Date(clockIn);
    const now = new Date();
    const diff = Math.floor((now - start) / 1000 / 60);
    const hours = Math.floor(diff / 60);
    const minutes = diff % 60;
    return `${hours}h ${minutes}m`;
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-64">
          <p className="text-foreground-muted">Loading...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="space-y-6" data-testid="dashboard-page">
        <div>
          <h1 className="text-3xl font-heading font-bold text-foreground-DEFAULT mb-2">
            Welcome back, {user.name}!
          </h1>
          <p className="text-foreground-muted">Here's your activity overview</p>
        </div>

        {user.role === 'admin' && pendingLeaves.length > 0 && showLeaveBanner && (
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-4" data-testid="leave-notification-banner">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3 flex-1">
                <AlertCircle className="w-5 h-5 text-orange-600 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-semibold text-orange-900 mb-1">
                    {pendingLeaves.length} Pending Leave Request{pendingLeaves.length > 1 ? 's' : ''}
                  </h3>
                  <div className="space-y-2">
                    {pendingLeaves.slice(0, 3).map((leave) => (
                      <div key={leave.leave_id} className="bg-white rounded-lg p-3 flex items-center justify-between">
                        <div>
                          <p className="font-medium text-sm text-foreground-DEFAULT">{leave.user_name}</p>
                          <p className="text-xs text-foreground-muted">
                            {leave.start_date} to {leave.end_date} ({leave.leave_type})
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleApprove(leave.leave_id)}
                            className="px-3 py-1 bg-accent text-white rounded-full text-xs font-medium hover:bg-accent/90"
                            data-testid={`quick-approve-${leave.leave_id}`}
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => handleReject(leave.leave_id)}
                            className="px-3 py-1 bg-destructive text-white rounded-full text-xs font-medium hover:bg-destructive/90"
                            data-testid={`quick-reject-${leave.leave_id}`}
                          >
                            Reject
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  {pendingLeaves.length > 3 && (
                    <button
                      onClick={() => navigate('/leave')}
                      className="text-sm text-orange-700 hover:text-orange-800 font-medium mt-2"
                    >
                      View all {pendingLeaves.length} requests →
                    </button>
                  )}
                </div>
              </div>
              <button
                onClick={() => setShowLeaveBanner(false)}
                className="text-orange-600 hover:text-orange-800"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <div className="md:col-span-5">
            <div className="bg-white border border-slate-100 shadow-sm rounded-2xl p-8 flex flex-col items-center justify-center" data-testid="timer-widget">
              <div className="mb-6">
                <p className="text-sm font-medium text-foreground-muted text-center mb-2">
                  {currentEntry ? 'Currently Working' : 'Not Clocked In'}
                </p>
                {currentEntry && (
                  <p className="text-4xl font-heading font-bold text-foreground-DEFAULT text-center">
                    {formatDuration(currentEntry.clock_in)}
                  </p>
                )}
              </div>

              {currentEntry ? (
                <button
                  data-testid="clock-out-btn"
                  onClick={handleClockOut}
                  className="bg-destructive text-white rounded-full w-32 h-32 flex flex-col items-center justify-center text-xl font-bold shadow-xl shadow-destructive/20 hover:scale-105 transition-transform"
                >
                  <Clock className="w-8 h-8 mb-2" />
                  Clock Out
                </button>
              ) : (
                <button
                  data-testid="clock-in-btn"
                  onClick={handleClockIn}
                  className="bg-accent text-white rounded-full w-32 h-32 flex flex-col items-center justify-center text-xl font-bold shadow-xl shadow-accent/20 hover:scale-105 transition-transform"
                >
                  <Clock className="w-8 h-8 mb-2" />
                  Clock In
                </button>
              )}
            </div>
          </div>

          <div className="md:col-span-7">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {user.role === 'admin' ? (
                <>
                  <div className="bg-white border border-slate-100 shadow-sm rounded-2xl p-6" data-testid="stat-card-employees">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="bg-primary/10 rounded-xl p-3">
                        <Users className="w-6 h-6 text-primary" />
                      </div>
                      <h3 className="font-semibold text-foreground-DEFAULT">Total Employees</h3>
                    </div>
                    <p className="text-3xl font-heading font-bold text-foreground-DEFAULT">
                      {summary?.total_employees || 0}
                    </p>
                  </div>

                  <div className="bg-white border border-slate-100 shadow-sm rounded-2xl p-6" data-testid="stat-card-active">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="bg-accent/10 rounded-xl p-3">
                        <Clock className="w-6 h-6 text-accent" />
                      </div>
                      <h3 className="font-semibold text-foreground-DEFAULT">Active Now</h3>
                    </div>
                    <p className="text-3xl font-heading font-bold text-foreground-DEFAULT">
                      {summary?.active_today || 0}
                    </p>
                  </div>

                  <div className="bg-white border border-slate-100 shadow-sm rounded-2xl p-6" data-testid="stat-card-leaves">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="bg-orange-100 rounded-xl p-3">
                        <Calendar className="w-6 h-6 text-orange-600" />
                      </div>
                      <h3 className="font-semibold text-foreground-DEFAULT">Pending Leaves</h3>
                    </div>
                    <p className="text-3xl font-heading font-bold text-foreground-DEFAULT">
                      {summary?.pending_leaves || 0}
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <div className="bg-white border border-slate-100 shadow-sm rounded-2xl p-6" data-testid="stat-card-hours">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="bg-primary/10 rounded-xl p-3">
                        <TrendingUp className="w-6 h-6 text-primary" />
                      </div>
                      <h3 className="font-semibold text-foreground-DEFAULT">Total Hours</h3>
                    </div>
                    <p className="text-3xl font-heading font-bold text-foreground-DEFAULT">
                      {summary?.total_hours || 0}h
                    </p>
                  </div>

                  <div className="bg-white border border-slate-100 shadow-sm rounded-2xl p-6" data-testid="stat-card-entries">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="bg-accent/10 rounded-xl p-3">
                        <Calendar className="w-6 h-6 text-accent" />
                      </div>
                      <h3 className="font-semibold text-foreground-DEFAULT">Total Entries</h3>
                    </div>
                    <p className="text-3xl font-heading font-bold text-foreground-DEFAULT">
                      {summary?.total_entries || 0}
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-100 shadow-sm rounded-2xl p-6" data-testid="recent-activity">
          <h2 className="text-xl font-heading font-bold text-foreground-DEFAULT mb-4">Recent Activity</h2>
          {recentActivity.length === 0 ? (
            <p className="text-foreground-muted text-center py-8">No activity yet</p>
          ) : (
            <div className="space-y-3">
              {recentActivity.map((entry) => (
                <div
                  key={entry.entry_id}
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-xl"
                  data-testid={`activity-entry-${entry.entry_id}`}
                >
                  <div>
                    <p className="font-medium text-foreground-DEFAULT">
                      {new Date(entry.clock_in).toLocaleDateString('en-US', {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </p>
                    <p className="text-sm text-foreground-muted">
                      {new Date(entry.clock_in).toLocaleTimeString('en-US', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                      {entry.clock_out && (
                        <>
                          {' - '}
                          {new Date(entry.clock_out).toLocaleTimeString('en-US', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </>
                      )}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-foreground-DEFAULT">
                      {entry.duration ? `${Math.floor(entry.duration / 60)}h ${entry.duration % 60}m` : 'In Progress'}
                    </p>
                    <span
                      className={`text-xs px-2 py-1 rounded-full ${
                        entry.status === 'active'
                          ? 'bg-accent/10 text-accent'
                          : 'bg-slate-200 text-slate-700'
                      }`}
                    >
                      {entry.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

export default Dashboard;